/**
 * Geometrie der Ausweichsituationen.
 *
 * Alles hängt an einer Frage: Was sieht der eine vom anderen? Genau das entscheidet in
 * den Regeln 13 bis 15, wer ausweicht, und genau das rechnet dieses Modul aus den
 * Kursen und der Peilung. Kompasskonvention: 0 Grad ist Nord, im Uhrzeigersinn.
 */
import { normaliseBearing, sectorVisibility } from './geometry'
import { lightState } from './data'
import type { LightType } from './types'

export interface Placement {
  /** Kurs über Grund in Grad, 0 = Nord. */
  headingDeg: number
  /** Peilung des anderen Fahrzeugs, relativ zum eigenen Kurs. */
  bearingDeg?: number
  distanceNm?: number
}

/** Ort des anderen Fahrzeugs in Seemeilen, x nach Osten, y nach Norden. */
export function positionOf(own: Placement, other: Placement): { x: number; y: number } {
  const compass = normaliseBearing(own.headingDeg + (other.bearingDeg ?? 0))
  const rad = (compass * Math.PI) / 180
  const distance = other.distanceNm ?? 1
  return { x: Math.sin(rad) * distance, y: Math.cos(rad) * distance }
}

/** Peilung, unter der das eigene Fahrzeug vom anderen aus gesehen wird, relativ zu dessen Kurs. */
export function bearingFromOther(own: Placement, other: Placement): number {
  const p = positionOf(own, other)
  const towardsOwn = normaliseBearing((Math.atan2(-p.x, -p.y) * 180) / Math.PI)
  return normaliseBearing(towardsOwn - other.headingDeg)
}

/** Welche Lichter des anderen Fahrzeugs sind vom eigenen aus zu sehen? */
export function lightsSeen(otherStateId: string, own: Placement, other: Placement): LightType[] {
  const bearing = bearingFromOther(own, other)
  const state = lightState(otherStateId)
  const seen = new Set<LightType>()
  for (const light of state.lights) {
    if (sectorVisibility(light.type, bearing, 0) > 0) seen.add(light.type)
  }
  return [...seen]
}

/** Sieht das eigene Fahrzeug beide Seitenlichter, steht man Bug auf Bug. */
export function seesBothSidelights(otherStateId: string, own: Placement, other: Placement): boolean {
  const seen = lightsSeen(otherStateId, own, other)
  return seen.includes('sidelight-port') && seen.includes('sidelight-stbd')
}

/**
 * Überholt das eigene Fahrzeug? Regel 13(b): überholend ist, wer sich mehr als
 * 22,5 Grad achterlicher als querab nähert, also aus dem Sektor des Hecklichts.
 */
export function isOvertaking(own: Placement, other: Placement): boolean {
  return sectorVisibility('sternlight', bearingFromOther(own, other), 0) > 0
}
